import { Component, OnInit, Input, Output, EventEmitter, ViewChild, ElementRef, ViewEncapsulation, HostListener } from "@angular/core";
import {conditionalValidator} from '@irb-common/validators/conditionalValidator';
import { FormControl } from "@angular/forms";
import { debounceTime, distinctUntilChanged } from "rxjs/operators";
import { DropdownFilterGridLayoutService } from "@irb-common/components/dropdown-filter/dropdown-filter.grid-layout.service";
import { IDropdownItem } from "@irb-common/models/dropdown.model";
//import { AllModules, Module } from 'ag-grid-enterprise';
import { HoverPanelService } from "@irb-common/components/hover-panel/hover-panel.service";
import { TooltipService } from '@irb-common/components/tooltip/tooltip.service';
import { DecodeHtmlPipe } from '@irb-common/pipes/DecodeHtmlPipe';


@Component({ standalone: false,
  selector: "irb-dropdown-filter",
  templateUrl: "./dropdown-filter.component.html",
  styleUrls: ["./dropdown-filter.component.scss"],
  encapsulation: ViewEncapsulation.None,
  providers: [DecodeHtmlPipe]
})
export class DropdownFilterComponent implements OnInit {
  @ViewChild("input") input: ElementRef;
  private _isRequired: boolean = false;
  private _value: IDropdownItem = null;
  private _disabled: boolean = false;
  @Input() placeholder: string = "-Select-";
  @Input() size: number = 25;
  @Input() hoverPanelZIndex: number;
  @Input() hoverPanelWidth: number = 300;
  @Output() valueChange = new EventEmitter<IDropdownItem>();
  @Output() search = new EventEmitter<string>();
  @Output() selection = new EventEmitter<IDropdownItem>();
  
  @Input()
  set isRequired(value: boolean) {
    this._isRequired = value || false;
    this.applyValidators();
  }

  get isRequired(): boolean {
    return this._isRequired;
  }
  
  inputControl = new FormControl();
  grid: any;
  gridOptions: any;
  gridHeight: string = "";
  searchString: string;
  selectedRowIndex: number;
  //modules: Module[] = AllModules;
  @Input() addNewItem: any;
  showAddNewButton: boolean = false;
  @Output() openNewItemFlow = new EventEmitter<any>();
  frameworkComponents: any;

  constructor(private gls: DropdownFilterGridLayoutService, private hps: HoverPanelService, private tts: TooltipService, private dhp: DecodeHtmlPipe) {}

  ngOnInit() {
    this.frameworkComponents = this.gls.getFrameworkComponents();
    this.gridOptions = {
      columnDefs: this.gls.getColumnDefs(),      
      suppressMovableColumns: true,
      gridAutoHeight: false,
      overlayLoadingTemplate: "<div class='loading-spinner'></div>",
      overlayNoRowsTemplate: "<span class='ag-overlay-loading-center ag-overlay-message'>No results found.</span>",
      rowSelection: "single",
    };
    
    this.inputControl.valueChanges.pipe(debounceTime(500), distinctUntilChanged()).subscribe((val) => {
      if ((this.value == null || val !== this.value.name) && val != null) {
        this.onValueUpdate(val);
      }
    });

    this.setInputValue();
    this.setDisabled();
  }

  @Input() set value(v: IDropdownItem) {
    this._value = v;
    this.setInputValue();
  }

  get value(): IDropdownItem {
    return this._value;
  }

  @Input() set disabled(v: boolean) {
    this._disabled = v;
    this.setDisabled();
  }

  get disabled(): boolean {
    return this._disabled;
  }

  onGridReady(params: any) {
    this.grid = params.api;
    this.onValueUpdate(null);
    this.selectDefaultRow();
    //setTimeout(() => this.grid.sizeColumnsToFit());
    this.updateGridHeight();
  }

  openOnClick() {
    if (this.grid && !this._disabled) {
      this.onValueUpdate(null);
    }
    if (this.input && this.input.nativeElement) {
      this.input.nativeElement.select();
    }
  }

  focusInput() {
    setTimeout(() => {
      this.input.nativeElement.focus();
      const s = typeof this.inputControl.value === "string" ? this.inputControl.value : "";
      //this.input.nativeElement.setSelectionRange(s.length, s.length);
      this.input.nativeElement.click();
    });
  }

  hide() {
    this.tts.remove();
    this.hps.remove();
    this.grid = null;
    this.setInputValue();
  }

  onRowClicked(e: any) {
    this.hide();
    if (!e.node.data.notClickableItem) {
      this.setValue(e.node.data);
      this.selection.emit(this.value);
    }
  }

  setValue(n: IDropdownItem) {
    this.value = n;
    this.valueChange.emit(this.value);
    this.setInputValue();
  }

  setInputValue() {
    if(this.value && this.value.name) {
      this.value.name = this.dhp.transform(this.value.name);
      if (this.value.class && this.value.class.length && this.value.class[0] === 'dd-msstar-recommended') {
        let displayedVal = this.value.name.replace('*', '');
        this.inputControl.setValue(displayedVal);
        return
      }
    }

    this.inputControl.setValue(this.value?.name || null);
  }

  onValueUpdate(s: any) {
    this.showAddNewButton = false;
    s = !s ? null : s;

    if (typeof s === "string" || s === null) {
      if (this.grid) {
        this.grid.setGridOption("rowData", []);
        this.grid.showLoadingOverlay();
      }

      this.searchString = s;
      this.search.emit(s);
    }
  }

  set(m: Array<IDropdownItem>) {
    m = m || [];

    m.forEach((obj: any) => {
      obj["match"] = this.searchString || "";
    });

    if (this.grid) {
      if (this.addNewItem && m.length === 0) {
        this.showAddNewButton = m.length === 0;
      }
      this.grid.setGridOption("rowData", m);
      this.selectDefaultRow();
      //this.grid.sizeColumnsToFit();
      this.updateGridHeight();
    }
  }

  selectDefaultRow() {
    this.grid?.forEachNode((node: any) => {
      if (node.data.value === this.value?.value) {
            this.selectRow(node.rowIndex);
          }
        });
      }

  incrementRowSelection() {
    this.selectRow(++this.selectedRowIndex);
  }

  decrementRowSelection() {
    this.selectRow(--this.selectedRowIndex);
  }

  selectRow(index: number) {
    if (index < 0) {
      index = 0;
    } else if (index > this.grid.getDisplayedRowCount() - 1) {
      index = this.grid.getDisplayedRowCount() - 1;
    }

    window.setTimeout(() => {
      if (index >= 0) {
        this.selectedRowIndex = index;
        if(this.grid) {
          this.grid.forEachNode((node: any) => node.setSelected(node.rowIndex === index));
          this.grid.ensureIndexVisible(index);
        }
      }
    }, 0);
  }

  onKeyUp(event: any) {
    switch (event.key) {
      case "ArrowDown":
        this.incrementRowSelection();
        break;
      case "ArrowUp":
        this.decrementRowSelection();
        break;
      case "Enter":
        if(this.grid) {
          const data = this.grid.getSelectedRows();
          this.hide();

          if (data.length > 0) {
            this.setValue(data[0]);
            this.selection.emit(data[0]);
          }
        }
        break;
    }
  }

  setDisabled() {
    this._disabled ? this.inputControl.disable() : this.inputControl.enable();
  }

  updateGridHeight() {
    const tr = this.grid.getDisplayedRowCount();

    if (tr === 0) {
      this.gridHeight = "90px";
    } else if (tr > 6) {
      this.gridHeight = "180px";
    } else {
      const p = tr * 30 + 3;
      this.gridHeight = p + "px";
    }

    this.hps.resize();
  }

  @HostListener("window:mousedown", ["$event"])
  onWindowClick(e: any) {
    if (this.input.nativeElement.contains(e.target)) {
      if(this.inputControl.value && typeof this.inputControl.value === "string" && this.inputControl.value.length > 0) {
        this.onValueUpdate(this.inputControl.value);
      } else {
        this.hide();
      }
    }
  }

  onHoverPanelClose() {
    this.hide();
  }
  
  addNewItemClick() {
    let dataParam: any = {
      dropdownSearch: true,
      dropdownSearchValue: this.inputControl.value
    };
    this.openNewItemFlow.emit(dataParam);
    this.showAddNewButton = false;
    this.hide();
  }

  static getFilteredList(searchStr: any, items: Array<any>): Array<any> {
    let f: Array<any> = [];
    let f1: Array<any> = [];
    let f2: Array<any> = [];
    if(searchStr) {
      searchStr = searchStr.replace(/\$/g, '\\$');
      if (searchStr.lastIndexOf('.') !== -1)
        searchStr = searchStr.replace(/\./g, '\\.');
      searchStr = searchStr.replace(/\(/g, '\\(');
      searchStr = searchStr.replace(/\)/g, '\\)');
      const r = new RegExp(searchStr, "gi");

      items.forEach((item: any) => {
        const name = item.name || '';
        const s = searchStr || ''
        if (item.name.match(r)) {
          if (name.toLowerCase().startsWith(s.toLowerCase())) {
            f1.push(item);
          } else {
            f2.push(item);
          }
        }
      });
      f=f1.concat(f2);
    } else {
      f = [].concat(items);
    }
    return f;
  }

  applyValidators() {
    if (this.inputControl && this.inputControl.setValidators) {
      const v = this.getValidators();
      this.inputControl.setValidators(v);
      this.inputControl.updateValueAndValidity();
    }
  }

  getValidators() {
    const v: any = [];
    
    v.push(conditionalValidator(() => this._isRequired, (c: FormControl) => {
      const hasValue = !(this.inputControl.value === null || typeof this.inputControl.value === "undefined" || this.inputControl.value == "");
      let r = null;

      if(c.touched) {
        r = !hasValue ? {'violatesRequired': {value: this.inputControl.value}} : null;
      }

      return r;
    }));

    return v;
  }
}
